/* eslint-disable no-unused-vars */
/* eslint-disable react/button-has-type */
import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize, useDetectScroll } from '@saviynt/common';
import {
  Box,
  Button,
  ButtonIcon,
  FooterActionBar,
  Icon,
  InlineMessage,
  Loader,
  MobileDrawer,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';
import PropTypes from 'prop-types';

import useResponsive from '../../../../hooks/use-responsive';
import {
  getEmailHistory,
  setCertsListEmailHistory,
} from '../../../../redux/certlist/action';
import { useInvertedLocalize } from '../../../../utils/localization';
import { getMessageKey } from '../../../../utils/utils';

import { invertedMessages } from './messages';

import './CertsListEmailHistory.css';

function EmailAccordion({ emailData }) {
  const [openSection, setOpenSection] = useState(null);

  const toggleSection = (id) => {
    // Todo | CR-17901 | Remove this once Dates avilable in Email History
    // setOpenSection((prev) => (prev === id ? null : id));
  };

  return (
    <Box className='CertsListEmailAccordion'>
      {emailData?.map((reminder) => (
        <Box key={reminder.id} className='CertsListEmailAccordion-section'>
          <button
            onClick={() => toggleSection(reminder.id)}
            className='CertsListEmailAccordion-buttonBox'>
            <Box className='CertsListEmailAccordion-titleWithIcon'>
              <Icon
                kind={
                  reminder.completed
                    ? 'CheckCircleFilled'
                    : 'CheckCircleOutline'
                }
                color={reminder.completed ? 'success-700' : 'neutral-1000'}
              />
              <Typography kind='h4' desktop='h4-d' mobile='h4-d'>
                {reminder.title}
              </Typography>
            </Box>
            {/* Todo | CR-17901 | Remove this once Dates avilable in Email History */}
            {/* <Icon
              kind={openSection === reminder.id ? 'ChevronUp' : 'ChevronDown'}
              color='neutral-1000'
            /> */}
          </button>

          {openSection === reminder.id && (
            <Box className='CertsListEmailAccordion-content'>
              <Typography kind='body3' desktop='body3-d' mobile='body3-d'>
                {reminder.status}
              </Typography>
              <Typography kind='body2' desktop='body2-d' mobile='body2-d'>
                {reminder.date}
              </Typography>
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
}

EmailAccordion.propTypes = {
  emailData: PropTypes.shape([]).isRequired,
};

function CertsListEmailHistory({ isOpen, setIsOpen, certKey }) {
  const intl = useIntl();
  const dispatch = useDispatch();
  const closeButtonRef = useRef(null);
  const l = useInvertedLocalize(invertedMessages);

  const { isMobileXlargeWidth } = useResponsive();
  const { isScrolled } = useDetectScroll();

  const { lineAction: { certsListEmailHistory, certsActionIsLoading } = {} } =
    useSelector((state) => state?.certifications?.list);

  const { messages } = useSelector((state) => state?.user);

  const title =
    messages.CERTS_LIST_ACTION_EMAIL_HISTORY_TITLE ||
    localize(intl, getMessageKey('CERTS_LIST_ACTION_EMAIL_HISTORY_TITLE'));
  const subtitle =
    messages.CERTS_LIST_ACTION_EMAIL_HISTORY_SUBTITLE ||
    localize(intl, getMessageKey('CERTS_LIST_ACTION_EMAIL_HISTORY_SUBTITLE'));

  const handleClose = () => {
    setIsOpen(false);
    dispatch(setCertsListEmailHistory(null));
  };

  useEffect(() => {
    if (isOpen) dispatch(getEmailHistory(certKey));
  }, [certKey]);

  const getBodyContent = () => {
    const { status, response } = certsListEmailHistory || {};

    if (certsActionIsLoading) {
      return (
        <div className='CertListExportLoader'>
          <Loader kind='dots' format='inline' color='brand' />
        </div>
      );
    }

    if (status === 'SUCCESS') {
      if (typeof response === 'string') {
        return (
          <div className='CertListEmailHistoryMessage'>
            <InlineMessage
              text={l(response)}
              colorTheme='info'
              size='medium'
              isVisible
              leftIcon={<Icon kind='AlertInfoFilled' />}
            />
          </div>
        );
      }

      const emailData = Object.entries(response).map(([key, value], index) => ({
        id: index,
        title: key,
        completed: value,
        status: 'sent',
        date: 'mm/dd/yyyy, hh:mm',
      }));

      return <EmailAccordion emailData={emailData} />;
    }

    if (status === 'FAILED') {
      return (
        <InlineMessage
          text={l(response)}
          colorTheme='critical'
          size='medium'
          isVisible
          leftIcon={<Icon kind='AlertCriticalFilled' />}
        />
      );
    }

    return null;
  };

  if (isMobileXlargeWidth) {
    const getHeaderContentMobile = () => (
      <>
        <div className='CertsListEmailHistory-mobileHeader'>
          <Icon kind='Mail' color='neutral-1000' size='large' />
          <div className=''>
            <Typography kind='h1' desktop='h1-d' mobile='h1-d'>
              {title}
            </Typography>
            <Typography kind='body3' desktop='body3-d' mobile='body3-d'>
              {subtitle}
            </Typography>
          </div>
        </div>

        <ButtonIcon
          ref={closeButtonRef}
          size='medium'
          kind='ghost'
          className='Example-MobileDrawer-closeButton'
          ariaLabel={l('Close')}
          icon={<Icon kind='Close' />}
          onClick={handleClose}
        />
      </>
    );

    const getFooterActionBarMobile = () => (
      <FooterActionBar isScrolled={isScrolled}>
        <Button
          kind='filled'
          onClick={handleClose}
          className='CertsListEmailHistory-mobileFooter--close'>
          {l('Close')}
        </Button>
      </FooterActionBar>
    );

    return (
      <MobileDrawer
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        isCloseButtonEnabled
        content={
          <Box className='CertsListEmailHistory-mobileBody'>
            {getBodyContent()}
          </Box>
        }
        headerContent={getHeaderContentMobile()}
        footerActionBar={getFooterActionBarMobile()}
        className='CertsListEmailHistory-mobileDrawer'
      />
    );
  }

  const certListEmailHistoryFooter = () => (
    <FooterActionBar className='CertsReassignModal-footer' size='large'>
      <div className='CertsReassignModal-actionButtons'>
        <Button
          className='CertsReassignModal-submitButton'
          type='button'
          kind='filled'
          size='medium'
          onClick={handleClose}
          isLoading={false}
          loaderText={l('Close')}>
          {l('Close')}
        </Button>
      </div>
    </FooterActionBar>
  );

  return (
    <ModalIsland
      isOpen={isOpen}
      onClose={handleClose}
      headerIcon={<Icon kind='Mail' color='neutral-1000' size='large' />}
      title={title}
      subtitle={subtitle}
      contentBody={getBodyContent()}
      FooterActionBarComp={certListEmailHistoryFooter()}
      className='CertsListEmailHistory'
    />
  );
}

CertsListEmailHistory.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  certKey: PropTypes.number.isRequired,
};

export default CertsListEmailHistory;
